import useSWR from 'swr'
import {
  Coupon,
  CouponDiscount,
  CouponsResponse,
  EventTicket,
  getCoupons,
  getCouponDiscounts,
  getEventTickets,
} from '../../Service/coupons'

/**
 * Fallback estável para o estado "sem dados". Um `?? []` inline cria um array
 * novo a cada render; como os consumidores usam essas listas em deps de
 * useEffect que chamam setState, isso gera loop infinito ("Maximum update depth
 * exceeded"). Reusar a mesma referência evita o loop.
 */
const EMPTY: never[] = []

/**
 * Cupons do evento (etapa 1). Traz também o slug usado para montar o link
 * de compra: https://www.bilheteriadigital.com/{slug}?c={comissarioId}
 */
export const useCoupons = (eventId: string | undefined) => {
  const { data, error, isLoading, mutate } = useSWR<CouponsResponse>(
    eventId ? ['coupons', eventId] : null,
    () => getCoupons(eventId as string),
    { revalidateOnFocus: false }
  )

  return {
    coupons: (data?.coupons ?? EMPTY) as Coupon[],
    slug: data?.slug ?? null,
    couponsLoading: isLoading,
    couponsError: error,
    refetchCoupons: mutate,
  }
}

/** Ingressos do evento (dropdown da etapa 2). */
export const useEventTickets = (eventId: string | undefined) => {
  const { data, error, isLoading } = useSWR<EventTicket[]>(
    eventId ? ['eventTickets', eventId] : null,
    () => getEventTickets(eventId as string),
    { revalidateOnFocus: false }
  )

  return {
    tickets: data ?? EMPTY,
    ticketsLoading: isLoading,
    ticketsError: error,
  }
}

/**
 * Cupons + desconto atual do ingresso selecionado (grid da etapa 2).
 * O backend já devolve SOMENTE cupons com código — regra fixa, não é filtro de UI.
 */
export const useCouponDiscounts = (
  eventId: string | undefined,
  ingressoId: number | null
) => {
  const { data, error, isLoading, mutate } = useSWR<CouponDiscount[]>(
    eventId && ingressoId ? ['couponDiscounts', eventId, ingressoId] : null,
    () => getCouponDiscounts(eventId as string, ingressoId as number),
    { revalidateOnFocus: false }
  )

  return {
    discounts: data ?? EMPTY,
    discountsLoading: isLoading,
    discountsError: error,
    refetchDiscounts: mutate,
  }
}
